import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { Flame, MapPin, ArrowUpRight, CheckCircle2, ChevronDown, ChevronUp, CircleDot, Target } from "lucide-react";
import EngagementBadge from "@/components/EngagementBadge";
import SessionMetaRow from "@/components/SessionMetaRow";

// Phase 25.13ab — Auto-surfaces the most engaging visitor sessions
// across all countries / referrers. Sits above the existing 2-col grid
// on AdminVisitors so super-admin sees high-intent leads first without
// having to drill into each country/referrer one-by-one.
//
// Props:
//   days (int)
//   includeInternal (bool)
export default function HotSessionsPanel({ days, includeInternal }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const qs = new URLSearchParams({
      days: String(days),
      include_internal: includeInternal ? "true" : "false",
      limit: "15",
    }).toString();
    api.get(`/admin/visitors/hot-sessions?${qs}`)
      .then(r => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData({ sessions: [], hot_count: 0, warm_count: 0, total_sessions: 0 }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [days, includeInternal]);

  const hotCount = data?.hot_count ?? 0;
  const warmCount = data?.warm_count ?? 0;
  const watchlistCount = data?.watchlist_count ?? 0;

  return (
    <Panel title="// HOT SESSIONS · AUTO-RANKED BY ENGAGEMENT" testId="hot-sessions-panel">
      <div className="px-4 py-3 border-b border-zinc-900 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
            <Flame className="w-3 h-3 text-[#FF6B5C]" />
            <span className="text-[#FF6B5C]" data-testid="hot-sessions-hot-count">{hotCount} HOT</span>
            <span className="text-zinc-700">·</span>
            <span className="text-[#FFB000]" data-testid="hot-sessions-warm-count">{warmCount} WARM</span>
            {watchlistCount > 0 && (
              <>
                <span className="text-zinc-700">·</span>
                <span className="text-[#FF3B30] flex items-center gap-1 font-bold" data-testid="hot-sessions-watchlist-count">
                  <Target className="w-3 h-3" />
                  {watchlistCount} WATCHLIST
                </span>
              </>
            )}
            <span className="text-zinc-700">·</span>
            <span className="text-zinc-600">{data?.total_sessions ?? 0} total in {days}D</span>
          </div>
        </div>
        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-700">
          Score = pages + /security + /pricing + multi-day + intl + converted + returning + corporate + watchlist
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center font-mono text-xs text-zinc-600">Loading…</div>
      ) : !data || data.sessions.length === 0 ? (
        <div className="p-6 text-center font-mono text-xs text-zinc-600 leading-relaxed">
          No sessions in the last {days} days. Sessions show up here as soon as visitors hit at least 1 public page.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-900">
          {data.sessions.map((s, idx) => {
            const isOpen = expanded === s.session_id;
            const firstDay = (s.first_seen || "").slice(0, 10);
            const lastDay = (s.last_seen || "").slice(0, 10);
            return (
              <li key={s.session_id} data-testid={`hot-session-${idx}`}>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : s.session_id)}
                  className="w-full text-left px-4 py-3 hover:bg-zinc-950/40"
                  aria-expanded={isOpen}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-mono text-[9px] text-zinc-700 w-6">{String(idx + 1).padStart(2, "0")}</span>
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <span className="font-mono text-[11px] text-zinc-300 truncate">{s.landing_path}</span>
                        <EngagementBadge engagement={s.engagement} />
                        {s.country && (
                          <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 flex-shrink-0">
                            <MapPin className="w-2.5 h-2.5" /> {s.country}
                          </span>
                        )}
                        {s.landing_referrer && s.landing_referrer !== "(direct)" && (
                          <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 flex-shrink-0">
                            <ArrowUpRight className="w-2.5 h-2.5" /> {s.landing_referrer}
                          </span>
                        )}
                        {s.converted && (
                          <span className="font-mono text-[9px] uppercase tracking-widest text-[#00E676] flex items-center gap-1 flex-shrink-0 border border-[#00E676]/40 bg-[#00E676]/5 px-1.5 py-px">
                            <CheckCircle2 className="w-2.5 h-2.5" /> CONVERTED
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="font-mono text-[10px] text-[#FFB000]">{s.pageviews} pv</span>
                      {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
                    </div>
                  </div>
                  <div className="pl-8">
                    <SessionMetaRow session={s} />
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-zinc-900 px-4 py-3 bg-black/40">
                    {s.engagement?.signals?.length > 0 && (
                      <div className="mb-2 flex items-center gap-1.5 flex-wrap">
                        <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">Why hot:</span>
                        {s.engagement.signals.map((sig, j) => (
                          <span key={j} className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000] border border-[#FFB000]/30 px-1.5 py-px">
                            {sig}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mb-2">
                      Journey · {firstDay}{lastDay !== firstDay ? ` → ${lastDay}` : ""}
                    </div>
                    <ol className="space-y-1.5">
                      {s.journey.map((step, j) => (
                        <li key={j} className="flex items-center gap-2 font-mono text-[11px] text-zinc-400">
                          <CircleDot className="w-2.5 h-2.5 text-[#FFB000] flex-shrink-0" />
                          <span className="truncate">{step.path}</span>
                          {step.referrer && step.referrer !== "(direct)" && (
                            <span className="text-zinc-700 text-[10px]">← {step.referrer}</span>
                          )}
                          <span className="text-zinc-700 ml-auto">{(step.created_at || "").slice(11, 19)}Z</span>
                        </li>
                      ))}
                    </ol>
                    <div className="mt-3 pt-3 border-t border-zinc-900 font-mono text-[9px] uppercase tracking-widest text-zinc-700 break-all">
                      session_id: {s.session_id}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
